<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';
import { useStore, useStoreGetters } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import { copyTextToClipboard } from 'shared/helpers/clipboard';
import GrowthLinksAPI from 'dashboard/api/growthLinks';

import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';

// Growth Links é GRÁTIS (decisão 26/08) — sem gate de módulo.

const store = useStore();
const getters = useStoreGetters();
const router = useRouter();
const { t } = useI18n();

const accountId = getters.getCurrentAccountId.value;

const records = ref([]);
const isLoading = ref(true);
const togglingId = ref(null);
const showDeleteConfirmationPopup = ref(false);
const selectedLink = ref({});

const funnels = computed(() => getters['funnels/getFunnels'].value || []);

const tableHeaders = computed(() => [
  t('GROWTH_LINKS_MGMT.LIST.TABLE_HEADER.NAME'),
  t('GROWTH_LINKS_MGMT.LIST.TABLE_HEADER.STATUS'),
  t('GROWTH_LINKS_MGMT.LIST.TABLE_HEADER.FUNNEL'),
  t('GROWTH_LINKS_MGMT.LIST.TABLE_HEADER.CLICKS'),
  t('GROWTH_LINKS_MGMT.LIST.TABLE_HEADER.LEADS'),
  t('GROWTH_LINKS_MGMT.LIST.TABLE_HEADER.CONVERSION'),
  t('GROWTH_LINKS_MGMT.LIST.TABLE_HEADER.ACTIONS'),
]);

const funnelName = funnelId => {
  const funnel = funnels.value.find(item => item.id === funnelId);
  return funnel ? funnel.name : t('GROWTH_LINKS_MGMT.LIST.NO_FUNNEL');
};

const conversion = link => {
  const clicks = link.click_count || 0;
  const leads = link.lead_count || 0;
  return clicks > 0 ? Math.round((leads / clicks) * 100) : 0;
};

const deleteMessage = computed(() => ` ${selectedLink.value.name}?`);

const fetchLinks = async () => {
  isLoading.value = true;
  try {
    const { data } = await GrowthLinksAPI.get();
    records.value = data.payload;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('funnels/get');
  fetchLinks();
});

const goToNew = () =>
  router.push({ name: 'growth_links_new', params: { accountId } });

const goToEdit = link =>
  router.push({
    name: 'growth_links_edit',
    params: { accountId, linkId: link.id },
  });

const toggleActive = async link => {
  togglingId.value = link.id;
  try {
    const { data } = await GrowthLinksAPI.update(link.id, {
      active: !link.active,
    });
    const idx = records.value.findIndex(l => l.id === link.id);
    if (idx !== -1) records.value[idx] = data;
    useAlert(t('GROWTH_LINKS_MGMT.TOGGLE.SUCCESS'));
  } catch (error) {
    useAlert(t('GROWTH_LINKS_MGMT.TOGGLE.ERROR'));
  } finally {
    togglingId.value = null;
  }
};

const copyLink = async link => {
  try {
    await copyTextToClipboard(link.short_url);
    useAlert(t('GROWTH_LINKS_MGMT.RESULT.COPIED_TOAST'));
  } catch (error) {
    useAlert(t('GROWTH_LINKS_MGMT.TOGGLE.ERROR'));
  }
};

const openDeletePopup = link => {
  selectedLink.value = link;
  showDeleteConfirmationPopup.value = true;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};
const confirmDeletion = async () => {
  closeDeletePopup();
  try {
    await GrowthLinksAPI.delete(selectedLink.value.id);
    records.value = records.value.filter(l => l.id !== selectedLink.value.id);
    useAlert(t('GROWTH_LINKS_MGMT.DELETE.SUCCESS'));
  } catch (error) {
    useAlert(t('GROWTH_LINKS_MGMT.DELETE.ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('GROWTH_LINKS_MGMT.LOADING')"
    :no-records-found="!isLoading && !records.length"
    :no-records-message="$t('GROWTH_LINKS_MGMT.LIST.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        :title="$t('GROWTH_LINKS_MGMT.HEADER')"
        :description="$t('GROWTH_LINKS_MGMT.DESCRIPTION')"
      >
        <template #actions>
          <Button
            icon="i-lucide-circle-plus"
            :label="$t('GROWTH_LINKS_MGMT.NEW_BUTTON')"
            @click="goToNew"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <table class="min-w-full overflow-x-auto divide-y divide-n-weak">
        <thead>
          <th
            v-for="thHeader in tableHeaders"
            :key="thHeader"
            class="py-4 font-semibold text-left ltr:pr-4 rtl:pl-4 text-n-slate-11"
          >
            {{ thHeader }}
          </th>
        </thead>
        <tbody class="flex-1 divide-y divide-n-weak text-n-slate-12">
          <tr v-for="link in records" :key="link.id">
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <button
                class="font-medium break-words text-left text-n-slate-12 hover:text-n-brand"
                @click="goToEdit(link)"
              >
                {{ link.name }}
              </button>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4">
              <button
                class="px-2 py-1 text-xs font-medium rounded-full transition-colors"
                :class="
                  link.active
                    ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400'
                    : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'
                "
                :disabled="togglingId === link.id"
                @click="toggleActive(link)"
              >
                {{
                  link.active
                    ? $t('GROWTH_LINKS_MGMT.LIST.STATUS_ACTIVE')
                    : $t('GROWTH_LINKS_MGMT.LIST.STATUS_INACTIVE')
                }}
              </button>
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 text-sm text-n-slate-11">
              {{ funnelName(link.funnel_id) }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 tabular-nums text-n-slate-11">
              {{ link.click_count }}
            </td>
            <td
              class="py-4 ltr:pr-4 rtl:pl-4 tabular-nums font-medium text-n-slate-12"
            >
              {{ link.lead_count }}
            </td>
            <td class="py-4 ltr:pr-4 rtl:pl-4 tabular-nums text-n-slate-11">
              {{ `${conversion(link)}%` }}
            </td>
            <td class="py-4 min-w-xs">
              <div class="flex gap-1 justify-end">
                <Button
                  v-tooltip.top="$t('GROWTH_LINKS_MGMT.LIST.COPY_LINK')"
                  icon="i-lucide-copy"
                  slate
                  xs
                  faded
                  @click="copyLink(link)"
                />
                <Button
                  v-tooltip.top="$t('GROWTH_LINKS_MGMT.LIST.EDIT')"
                  icon="i-lucide-pen"
                  slate
                  xs
                  faded
                  @click="goToEdit(link)"
                />
                <Button
                  v-tooltip.top="$t('GROWTH_LINKS_MGMT.LIST.DELETE')"
                  icon="i-lucide-trash-2"
                  xs
                  ruby
                  faded
                  @click="openDeletePopup(link)"
                />
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </template>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('GROWTH_LINKS_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('GROWTH_LINKS_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="deleteMessage"
      :confirm-text="$t('GROWTH_LINKS_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('GROWTH_LINKS_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
